import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import MiniGame from '../components/MiniGame';
import ThemedCheckbox from '../components/ThemedCheckbox';
import {
  subscribeToGame,
  subscribeToRound,
  subscribeToScores,
  startRound,
  leaveGame,
  endGame,
  updateGameRules,
  toggleLobbyLock,
} from '../services/firebase/games';
import ListEditorModal from '../components/ListEditorModal';
import {
  subscribeToChatterProfile,
  subscribeToChatterProfiles,
  subscribeToChatterVotes,
} from '../services/firebase/chatters';
import { getUserProfile } from '../services/firebase/users';
import { getAvatarEmoji } from '../constants/avatars';
import { GAME_STATUS, ROUND_PHASE, CHATTERS_ID } from '../constants/game';
import { logError } from '../utils/logger';
import Layout from '../components/Layout';
import VisualizerCanvas from '../components/AudioVisualizer';
import { WebRTCProvider, useWebRTC } from '../context/WebRTCContext';
import PermissionWarningModal from '../components/PermissionWarningModal';
import WaitingRoom from './game/WaitingRoom';
import SongSelectionPhase from './game/SongSelectionPhase';
import SongQueuePanel, { getSongQueue } from './game/SongQueuePanel';
import ChatterSongSelectionPhase from './game/ChatterSongSelectionPhase';
import ScoringPhase from './game/ScoringPhase';
import ChatterScoringPhase from './game/ChatterScoringPhase';
import RevealingPhase from './game/RevealingPhase';
import RoundResults from './game/RoundResults';
import GameOver from './game/GameOver';

export default function GamePage() {
  const { t } = useTranslation();
  const { gameId } = useParams();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [round, setRound] = useState(null);
  const [scores, setScores] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [endGameConfirm, setEndGameConfirm] = useState(false);
  const [endingGame, setEndingGame] = useState(false);
  const [miniGameOpen, setMiniGameOpen] = useState(false);
  const [songQueueOpen, setSongQueueOpen] = useState(false);
  const [hideMiniGame, setHideMiniGame] = useState(() => localStorage.getItem('tmg_hide_minigame') === '1');
  const [hideQueue, setHideQueue] = useState(() => localStorage.getItem('tmg_hide_queue') === '1');
  const [hasQueuedSong, setHasQueuedSong] = useState(false);
  const [firefoxAutoplayDismissed, setFirefoxAutoplayDismissed] = useState(
    () => !!localStorage.getItem('tmg_firefox_autoplay_dismissed')
  );
  // Scoring phase screen-share maximise state — persisted across rounds
  const [scoringMaximized, setScoringMaximized] = useState(false);
  const [scoringFloatingPos, setScoringFloatingPos] = useState(null);
  const [scoringFloatingW, setScoringFloatingW] = useState(340);
  // Override tracks a new best set during this session; falls back to the profile value
  const [miniGameHighScoreOverride, setMiniGameHighScoreOverride] = useState(null);

  // Display mode — hides all ratings on this screen (for use on a shared device)
  const [displayMode, setDisplayMode] = useState(false);

  // Game controls dropdown menu
  const [gameControlsOpen, setGameControlsOpen] = useState(false);
  const gameControlsButtonRef = useRef(null);
  const gameControlsPanelRef = useRef(null);

  // In-game rules editor state (host only)
  const [rulesOpen, setRulesOpen] = useState(false);
  const [pendingRules, setPendingRules] = useState(null);
  const [rulesPanelStyle, setRulesPanelStyle] = useState({});
  // Which list-editor modal is open inside the rules panel
  const [rulesListModal, setRulesListModal] = useState(null);
  const rulesButtonRef = useRef(null);
  const rulesPanelRef = useRef(null);
  const rulesAutoSaveRef = useRef(null);

  // Close panels that live in the hidden headers when entering maximised mode
  useEffect(() => {
    if (scoringMaximized) {
      setRulesOpen(false);
      setLeaveConfirm(false);
      setEndGameConfirm(false);
    }
  }, [scoringMaximized]);

  // Close game controls menu on outside click or Escape
  useEffect(() => {
    if (!gameControlsOpen) return;
    function handleClick(e) {
      if (
        !gameControlsPanelRef.current?.contains(e.target) &&
        !gameControlsButtonRef.current?.contains(e.target)
      ) {
        setGameControlsOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setGameControlsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [gameControlsOpen]);

  // Close rules panel on outside click or Escape
  useEffect(() => {
    if (!rulesOpen) return;
    function handleClick(e) {
      if (rulesListModal) return;
      if (
        !rulesPanelRef.current?.contains(e.target) &&
        !rulesButtonRef.current?.contains(e.target)
      ) {
        setRulesOpen(false);
        setPendingRules(null);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') {
        setRulesOpen(false);
        setPendingRules(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [rulesOpen, rulesListModal]);

  // Auto-save rules whenever pendingRules changes (debounced 300ms)
  useEffect(() => {
    if (!pendingRules || !rulesOpen) return;
    clearTimeout(rulesAutoSaveRef.current);
    rulesAutoSaveRef.current = setTimeout(() => {
      updateGameRules(gameId, pendingRules).catch(logError);
    }, 300);
    return () => clearTimeout(rulesAutoSaveRef.current);
  }, [pendingRules, rulesOpen, gameId]);

  // Clamp rules panel so it never goes off the left edge of the viewport
  useLayoutEffect(() => {
    if (!rulesOpen || !rulesPanelRef.current) return;
    const panel = rulesPanelRef.current;
    const rect = panel.getBoundingClientRect();
    if (rect.left < 8) {
      panel.style.right = 'auto';
      panel.style.left = '8px';
    }
  }, [rulesOpen, rulesPanelStyle]);

  // Player avatar emojis — uid → emoji string, fetched once per game
  const [playerAvatars, setPlayerAvatars] = useState({});

  // Chatter state
  // undefined = loading, null = not a chatter, object = is a chatter
  const [chatterProfile, setChatterProfile] = useState(undefined);
  const [chatterProfiles, setChatterProfiles] = useState({});
  const [chatterVotes, setChatterVotes] = useState({});

  // Auto-start first round guard (prevents double-start in StrictMode or due to rapid Firestore snapshots)
  const hasStartedFirstRoundRef = useRef(false);

  // Fetch avatar emojis for all regular players whenever the player list changes
  useEffect(() => {
    if (!game?.playerIds) return;
    const regularIds = game.playerIds.filter((id) => id !== CHATTERS_ID);
    Promise.all(
      regularIds.map(async (uid) => {
        try {
          const p = await getUserProfile(uid);
          return [uid, getAvatarEmoji(p?.avatarKey)];
        } catch {
          return [uid, getAvatarEmoji(null)];
        }
      })
    ).then((entries) => setPlayerAvatars(Object.fromEntries(entries)));
  }, [game?.playerIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to game document
  useEffect(() => {
    if (!gameId) return;
    const unsub = subscribeToGame(gameId, (data) => {
      setGame(data);
    });
    const timeout = setTimeout(() => {
      setNotFound(true);
    }, 5000);
    return () => {
      unsub();
      clearTimeout(timeout);
    };
  }, [gameId]);

  // Subscribe to active round + scores
  useEffect(() => {
    if (!game?.activeRoundId) {
      setRound(null);
      setScores({});
      return;
    }
    const unsubRound = subscribeToRound(gameId, game.activeRoundId, setRound);
    const unsubScores = subscribeToScores(gameId, game.activeRoundId, setScores);
    return () => {
      unsubRound();
      unsubScores();
    };
  }, [gameId, game?.activeRoundId]);

  // Subscribe to own chatter profile (to detect chatter role)
  useEffect(() => {
    if (!gameId || !user?.uid) return;
    return subscribeToChatterProfile(gameId, user.uid, setChatterProfile);
  }, [gameId, user?.uid]);

  // Subscribe to all chatter profiles (for waiting room + chatter count)
  useEffect(() => {
    if (!gameId) return;
    return subscribeToChatterProfiles(gameId, setChatterProfiles);
  }, [gameId]);

  // Subscribe to chatter votes for the active round
  useEffect(() => {
    if (!game?.activeRoundId) {
      setChatterVotes({});
      return;
    }
    return subscribeToChatterVotes(gameId, game.activeRoundId, setChatterVotes);
  }, [gameId, game?.activeRoundId]);

  // Auto-start first round when game becomes active
  useEffect(() => {
    if (
      game?.status === GAME_STATUS.ACTIVE &&
      !game?.activeRoundId &&
      game?.hostId === user?.uid &&
      !hasStartedFirstRoundRef.current
    ) {
      hasStartedFirstRoundRef.current = true;
      handleStartFirstRound();
    }
  }, [game?.status, game?.activeRoundId, user?.uid, game?.hostId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRegularPlayer = game?.playerIds.includes(user?.uid);
  const isChatter = !isRegularPlayer && chatterProfile !== null && chatterProfile !== undefined;
  const isHost = game?.hostId === user?.uid;
  const isKicked = !!(game?.kickedPlayerIds?.includes(user?.uid));

  // Access is determined once: we're a regular player, OR the chatter profile has resolved
  const accessDetermined = isRegularPlayer || chatterProfile !== undefined;

  // Close song queue when it becomes hidden (phase change or chooser turn)
  const songQueueVisible = !isChatter && isRegularPlayer &&
    game?.status === GAME_STATUS.ACTIVE && !!round &&
    !(round?.phase === ROUND_PHASE.SONG_SELECTION && round?.chooserId === user?.uid);
  useEffect(() => {
    if (!songQueueVisible) setSongQueueOpen(false);
  }, [songQueueVisible]);

  // When the song queue button reappears (e.g. after the chooser's turn ends and the
  // queue was cleared by SongSelectionPhase), re-read from localStorage so the badge is accurate.
  useEffect(() => {
    if (songQueueVisible && gameId && user?.uid) {
      setHasQueuedSong(!!getSongQueue(gameId, user.uid));
    }
  }, [songQueueVisible, gameId, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whether ADHD minigame button/panel should be shown
  const miniGameShow =
    game?.status === GAME_STATUS.ACTIVE &&
    round != null &&
    (round.phase === ROUND_PHASE.SCORING ||
      (round.phase === ROUND_PHASE.SONG_SELECTION && round.chooserId !== user?.uid));
  useEffect(() => {
    if (!miniGameShow) setMiniGameOpen(false);
  }, [miniGameShow]);

  async function handleLeaveGame() {
    setLeaving(true);
    try {
      await leaveGame(gameId, user.uid);
      navigate('/lobby');
    } catch (err) {
      logError(err);
      setLeaving(false);
    }
  }

  async function handleEndGame() {
    setEndingGame(true);
    try {
      await endGame(gameId);
    } catch (err) {
      logError(err);
      setEndingGame(false);
    }
  }

  const DEFAULT_RULES = {
    noRepeatArtist: false,
    noRepeatSong: false,
    blacklistArtists: [],
    blacklistArtistsEnabled: false,
    whitelistArtists: [],
    whitelistArtistsEnabled: false,
    blacklistSongs: [],
    blacklistSongsEnabled: false,
    whitelistSongs: [],
    whitelistSongsEnabled: false,
  };


  async function handleStartFirstRound() {
    try {
      await startRound(gameId, { chooserId: game.hostId, song: null, currentRoundCount: game.roundCount });
    } catch (err) {
      logError(err);
    }
  }

  if (notFound && !game) {
    return (
      <Layout>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
          <p className="t-text-muted mb-4">{t('game.notFound')}</p>
          <button
            onClick={() => navigate('/lobby')}
            className="t-text-accent hover:opacity-80 underline t-focus-ring rounded"
          >
            {t('game.backToLobby')}
          </button>
        </div>
      </Layout>
    );
  }

  if (!game || !accessDetermined) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 t-border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Kicked players see a dedicated message
  if (isKicked) {
    return (
      <Layout>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
          <p className="text-2xl mb-3" aria-hidden="true">🚫</p>
          <p className="t-text font-semibold mb-2">{t('game.youWereKicked')}</p>
          <button
            onClick={() => navigate('/lobby')}
            className="t-text-accent hover:opacity-80 underline t-focus-ring rounded"
          >
            {t('game.backToLobby')}
          </button>
        </div>
      </Layout>
    );
  }

  // Redirect if user is neither a regular player nor a chatter (skip while leave is in progress)
  if (!isRegularPlayer && !isChatter && !leaving) {
    return (
      <Layout>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
          <p className="t-text-muted mb-4">{t('game.notAPlayer')}</p>
          <button
            onClick={() => navigate('/lobby')}
            className="t-text-accent hover:opacity-80 underline t-focus-ring rounded"
          >
            {t('game.backToLobby')}
          </button>
        </div>
      </Layout>
    );
  }

  // Compute header bar center labels from the active round/phase
  const headerRegularPlayerIds = (game?.playerIds ?? []).filter((id) => id !== CHATTERS_ID);
  let headerPhaseLabel = null;
  let headerSongLabel = null;
  if (game?.status === GAME_STATUS.ACTIVE && round) {
    const n = round.roundNumber;
    switch (round.phase) {
      case ROUND_PHASE.SONG_SELECTION:
        headerPhaseLabel = t('songSelection.round', { n });
        break;
      case ROUND_PHASE.SCORING: {
        const locked = headerRegularPlayerIds.filter((id) => scores[id]?.lockedIn).length;
        headerPhaseLabel = locked > 0
          ? `${t('scoring.roundScoring', { n })} · ${t('scoring.playersLockedIn', { locked, total: headerRegularPlayerIds.length })}`
          : t('scoring.roundScoring', { n });
        break;
      }
      case ROUND_PHASE.REVEALING: {
        const sub = round.revealPhase === 'scores' ? t('revealing.scores') : t('revealing.predictions');
        headerPhaseLabel = `${t('revealing.roundReveal', { n })} · ${sub}`;
        break;
      }
      case ROUND_PHASE.ROUND_RESULTS:
        headerPhaseLabel = t('roundResults.roundResults', { n });
        break;
      default:
        break;
    }
    if (round.song?.title && round.phase !== ROUND_PHASE.SONG_SELECTION) {
      headerSongLabel = round.song.artist
        ? `${round.song.title} — ${round.song.artist}`
        : round.song.title;
    }
  }

  // Settings menu extra — game button visibility toggles (regular players only, not chatters)
  const settingsExtra = isRegularPlayer && !isChatter ? (
    <>
      <div className="t-border border-t" />
      <div className="px-4 pt-3 pb-4 space-y-2">
        <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
          {t('game.gameButtons')}
        </p>
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => {
              const next = !hideMiniGame;
              setHideMiniGame(next);
              if (next) setMiniGameOpen(false);
              localStorage.setItem('tmg_hide_minigame', next ? '1' : '0');
            }}
            className="w-full text-left text-xs t-text-muted hover:t-text transition-colors t-focus-ring rounded py-0.5"
          >
            {hideMiniGame ? t('game.showMiniGameButton') : t('game.hideMiniGameButton')}
          </button>
          <button
            type="button"
            onClick={() => {
              const next = !hideQueue;
              setHideQueue(next);
              if (next) setSongQueueOpen(false);
              localStorage.setItem('tmg_hide_queue', next ? '1' : '0');
            }}
            className="w-full text-left text-xs t-text-muted hover:t-text transition-colors t-focus-ring rounded py-0.5"
          >
            {hideQueue ? t('game.showQueueButton') : t('game.hideQueueButton')}
          </button>
        </div>
      </div>
    </>
  ) : null;

  return (
    <Layout hideHeader={scoringMaximized} settingsExtra={settingsExtra}>
      <WebRTCProvider
        gameId={gameId}
        playerIds={game.playerIds}
        hostId={game.hostId}
        isChatter={isChatter}
        inPersonMode={game.inPersonMode}
        gameActive={game.status === GAME_STATUS.ACTIVE}
      >
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <VisualizerCanvas />
      {/* Game header bar — hidden in maximised mode to maximise screen space */}
      {!scoringMaximized && (
        <div className="t-border border-b px-4 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-x-3 text-xs t-text-muted relative">
          {/* Left: chatter badge */}
          <div className="flex items-center gap-2">
            {isChatter && (
              <span className="t-bg-warning t-text-warning px-2 py-0.5 rounded font-medium shrink-0">
                {t('game.chat')}
              </span>
            )}
          </div>

          {/* Center: Phase label + Song title — uses all available space, centered */}
          <div className="flex flex-row items-center justify-center gap-x-3 gap-y-0.5 text-center min-w-0 py-0.5 overflow-hidden">
            {headerPhaseLabel && (
              <span className="font-medium t-text-secondary whitespace-nowrap shrink-0">{headerPhaseLabel}</span>
            )}
            {headerSongLabel && (
              <span className="t-text-muted whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">{headerSongLabel}</span>
            )}
          </div>

          {/* Right: Game controls button */}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={gameControlsButtonRef}
              type="button"
              onClick={() => setGameControlsOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={gameControlsOpen}
              aria-label={t('game.gameControlsLabel')}
              className="flex items-center gap-1 t-text-muted hover:t-text-accent transition-colors t-focus-ring rounded"
            >
              <span>{t('game.gameControls')}</span>
              <span aria-hidden="true">▾</span>
            </button>
          </div>

          {/* Game controls dropdown menu */}
          {gameControlsOpen && (
            <div
              ref={gameControlsPanelRef}
              role="menu"
              className="absolute top-full right-0 z-50 mt-1 w-60 t-bg-surface t-border border rounded-xl shadow-xl overflow-hidden"
            >
              {/* Room code */}
              <div className="px-3 py-2.5 t-border border-b">
                <p className="text-xs t-text-disabled uppercase tracking-wide mb-1">{t('game.room')}</p>
                <CopyRoomCode code={game.code} />
              </div>

              {/* Game Rules (host only) */}
              {isHost && (
                <button
                  ref={rulesButtonRef}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    if (rulesButtonRef.current) {
                      const rect = rulesButtonRef.current.getBoundingClientRect();
                      setRulesPanelStyle({
                        position: 'fixed',
                        top: `${rect.bottom + 8}px`,
                        right: `${window.innerWidth - rect.right}px`,
                      });
                    }
                    setPendingRules({ ...DEFAULT_RULES, ...(game?.rules || {}) });
                    setGameControlsOpen(false);
                    setRulesOpen(true);
                  }}
                  aria-expanded={rulesOpen}
                  aria-controls="game-rules-panel"
                  className="w-full text-left px-3 py-2.5 t-text-muted hover:t-text hover:t-bg-hover transition-colors t-focus-ring"
                >
                  {t('game.gameRules')}
                </button>
              )}

              {/* Lock/Unlock Lobby (host only) */}
              {isHost && game.status !== GAME_STATUS.COMPLETED && (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={!!game.locked}
                  onClick={() => {
                    toggleLobbyLock(gameId, !game.locked).catch(logError);
                    setGameControlsOpen(false);
                  }}
                  className="w-full text-left px-3 py-2.5 t-text-muted hover:t-text hover:t-bg-hover transition-colors t-focus-ring flex items-center justify-between"
                >
                  <span>{game.locked ? t('game.unlockLobby') : t('game.lockLobby')}</span>
                  {game.locked && <span aria-hidden="true" className="t-text-accent">🔒</span>}
                </button>
              )}

              {/* Hide/Show Ratings */}
              {!isChatter && isRegularPlayer && (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={displayMode}
                  onClick={() => setDisplayMode((v) => !v)}
                  className="w-full text-left px-3 py-2.5 t-text-muted hover:t-text hover:t-bg-hover transition-colors t-focus-ring flex items-center justify-between"
                >
                  <span>{displayMode ? t('game.showRatings') : t('game.hideRatings')}</span>
                  {displayMode && <span aria-hidden="true" className="t-text-accent">✓</span>}
                </button>
              )}

              {/* Destructive actions (Leave / End) */}
              {game.status !== GAME_STATUS.COMPLETED && (isHost || (!isHost && isRegularPlayer)) && (
                <div className="t-border border-t">
                  {!isHost && isRegularPlayer && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setGameControlsOpen(false);
                        setLeaveConfirm(true);
                      }}
                      disabled={round?.phase === ROUND_PHASE.SONG_SELECTION && round?.chooserId === user?.uid}
                      title={
                        round?.phase === ROUND_PHASE.SONG_SELECTION && round?.chooserId === user?.uid
                          ? t('game.cannotLeaveChooser')
                          : undefined
                      }
                      className="w-full text-left px-3 py-2.5 t-text-error hover:opacity-80 transition-opacity t-focus-ring disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('game.leaveGame')}
                    </button>
                  )}
                  {isHost && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setGameControlsOpen(false);
                        setEndGameConfirm(true);
                      }}
                      className="w-full text-left px-3 py-2.5 t-text-error hover:opacity-80 transition-opacity t-focus-ring"
                    >
                      {t('game.endGame')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Firefox autoplay warning — shown in online mode only, dismissed via localStorage */}
      {!game.inPersonMode && !firefoxAutoplayDismissed && /firefox/i.test(navigator.userAgent) && (
        <div className="t-bg-surface t-border border-b px-4 py-2 flex items-center justify-between gap-3 flex-wrap text-xs t-text-secondary">
          <span>{t('game.firefoxAutoplayWarning')}</span>
          <button
            type="button"
            onClick={() => {
              localStorage.setItem('tmg_firefox_autoplay_dismissed', '1');
              setFirefoxAutoplayDismissed(true);
            }}
            aria-label={t('game.firefoxAutoplayDismiss')}
            className="t-text-muted hover:t-text transition-colors t-focus-ring rounded shrink-0"
          >
            ✕
          </button>
        </div>
      )}

      {/* Leave game confirmation bar */}
      {leaveConfirm && (
        <div className="t-border border-b px-4 py-2 flex items-center justify-end gap-3 flex-wrap text-xs t-bg-surface">
          <span className="t-text-error font-medium">{t('game.leaveGameConfirm')}</span>
          <button
            type="button"
            onClick={handleLeaveGame}
            disabled={leaving}
            className="t-btn-error disabled:opacity-50 px-2 py-1 rounded transition-colors t-focus-ring"
          >
            {leaving ? t('game.leaving') : t('game.yesLeaveGame')}
          </button>
          <button
            type="button"
            onClick={() => setLeaveConfirm(false)}
            className="t-text-disabled hover:t-text-muted transition-colors t-focus-ring rounded"
          >
            {t('game.cancel')}
          </button>
        </div>
      )}

      {/* End game confirmation bar (host only) */}
      {endGameConfirm && (
        <div className="t-border border-b px-4 py-2 flex items-center justify-end gap-3 flex-wrap text-xs t-bg-surface">
          <span className="t-text-error font-medium">{t('game.endGameConfirm')}</span>
          <button
            type="button"
            onClick={handleEndGame}
            disabled={endingGame}
            className="t-btn-error disabled:opacity-50 px-2 py-1 rounded transition-colors t-focus-ring"
          >
            {endingGame ? t('game.ending') : t('game.yesEndGame')}
          </button>
          <button
            type="button"
            onClick={() => setEndGameConfirm(false)}
            className="t-text-disabled hover:t-text-muted transition-colors t-focus-ring rounded"
          >
            {t('game.cancel')}
          </button>
        </div>
      )}

      {/* In-game rules editor panel (host only) — floating fixed popover */}
      {rulesOpen && isHost && pendingRules && (
        <div
          ref={rulesPanelRef}
          id="game-rules-panel"
          role="dialog"
          aria-label={t('game.editRules')}
          style={rulesPanelStyle}
          className="w-80 t-bg-surface t-border border rounded-xl shadow-xl z-50 text-sm overflow-y-auto max-h-[min(32rem,80dvh)]"
        >
          <div className="px-4 pt-3 pb-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium t-text-secondary">{t('game.editRules')}</p>
              <button
                type="button"
                onClick={() => { setRulesOpen(false); setPendingRules(null); }}
                aria-label={t('game.cancel')}
                className="t-text-muted hover:t-text transition-colors t-focus-ring rounded p-0.5"
              >✕</button>
            </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <ThemedCheckbox
              checked={pendingRules.noRepeatArtist}
              onChange={(e) => setPendingRules((r) => ({ ...r, noRepeatArtist: e.target.checked }))}
              className="mt-0.5 t-checkbox t-focus-ring"
            />
            <div>
              <p className="font-medium t-text-secondary">{t('game.rulesNoRepeatArtist')}</p>
              <p className="text-xs t-text-disabled mt-0.5">{t('game.rulesNoRepeatArtistDesc')}</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <ThemedCheckbox
              checked={pendingRules.noRepeatSong}
              onChange={(e) => setPendingRules((r) => ({ ...r, noRepeatSong: e.target.checked }))}
              className="mt-0.5 t-checkbox t-focus-ring"
            />
            <div>
              <p className="font-medium t-text-secondary">{t('game.rulesNoRepeatSong')}</p>
              <p className="text-xs t-text-disabled mt-0.5">{t('game.rulesNoRepeatSongDesc')}</p>
            </div>
          </label>

          {/* List-based rules */}
          {[
            { key: 'blacklistArtists', titleKey: 'game.rulesBlacklistArtists', descKey: 'game.rulesBlacklistArtistsDesc', btnKey: 'game.rulesSetArtists' },
            { key: 'whitelistArtists', titleKey: 'game.rulesWhitelistArtists', descKey: 'game.rulesWhitelistArtistsDesc', btnKey: 'game.rulesSetArtists' },
            { key: 'blacklistSongs',   titleKey: 'game.rulesBlacklistSongs',   descKey: 'game.rulesBlacklistSongsDesc',   btnKey: 'game.rulesSetSongs' },
            { key: 'whitelistSongs',   titleKey: 'game.rulesWhitelistSongs',   descKey: 'game.rulesWhitelistSongsDesc',   btnKey: 'game.rulesSetSongs' },
          ].map(({ key, titleKey, descKey, btnKey }) => {
            const items = pendingRules[key] || [];
            const enabledKey = `${key}Enabled`;
            const checkId = `game-rule-${key}`;
            return (
              <div key={key} className="flex items-start gap-3">
                <ThemedCheckbox
                  id={checkId}
                  checked={pendingRules[enabledKey] || false}
                  onChange={(e) => setPendingRules((r) => ({ ...r, [enabledKey]: e.target.checked }))}
                  className="mt-0.5 t-checkbox t-focus-ring"
                />
                <div className="flex-1 min-w-0 space-y-1">
                  <label htmlFor={checkId} className="cursor-pointer">
                    <p className="font-medium t-text-secondary">{t(titleKey)}</p>
                  </label>
                  <div className="flex items-start gap-2">
                    <p className="flex-1 min-w-0 text-xs t-text-disabled">{t(descKey)}</p>
                    <button
                      type="button"
                      onClick={() => setRulesListModal(key)}
                      className="shrink-0 t-btn-primary font-medium rounded-lg px-3 py-1.5 text-xs transition-colors t-focus-ring"
                    >
                      {t(btnKey)}
                    </button>
                  </div>
                  {items.length > 0 && (
                    <p className="text-xs t-text-accent">
                      {t('game.rulesItemsSet', { count: items.length })}: {items.slice(0, 3).join(', ')}{items.length > 3 ? '…' : ''}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          </div>
        </div>
      )}

      {/* List editor modal (in-game rules, host only) */}
      {rulesListModal && pendingRules && (
        <ListEditorModal
          title={t(`game.rules${rulesListModal.charAt(0).toUpperCase() + rulesListModal.slice(1)}`)}
          description={t(`game.rules${rulesListModal.charAt(0).toUpperCase() + rulesListModal.slice(1)}Desc`)}
          placeholder={
            rulesListModal.includes('Artists')
              ? 'e.g. Rick Astley, BTS, Queen'
              : 'e.g. Never Gonna Give You Up, Baby'
          }
          currentItems={pendingRules[rulesListModal] || []}
          onApply={(items) => {
            setPendingRules((r) => ({ ...r, [rulesListModal]: items }));
            setRulesListModal(null);
          }}
          onClose={() => setRulesListModal(null)}
        />
      )}

      {/* Waiting room */}
      {game.status === GAME_STATUS.WAITING && (
        <WaitingRoom
          game={game}
          isHost={isHost}
          isChatter={isChatter}
          chatterProfiles={chatterProfiles}
          playerAvatars={playerAvatars}
        />
      )}

      {/* Active game */}
      {game.status === GAME_STATUS.ACTIVE && (
        <>
          {/* Round phases */}
          {round && (
            <>
              {round.phase === ROUND_PHASE.SONG_SELECTION && (
                round.chooserId === CHATTERS_ID ? (
                  <ChatterSongSelectionPhase
                    game={game}
                    round={round}
                    isChatter={isChatter}
                    isHost={isHost}
                    chatterVotes={chatterVotes}
                  />
                ) : (
                  <SongSelectionPhase
                    game={game}
                    round={round}
                    playerAvatars={playerAvatars}
                    isHost={isHost}
                  />
                )
              )}
              {round.phase === ROUND_PHASE.SCORING && (
                isChatter ? (
                  <ChatterScoringPhase
                    game={game}
                    round={round}
                    chatterVotes={chatterVotes}
                    myVote={chatterVotes[user.uid]}
                  />
                ) : (
                  <ScoringPhase
                    game={game}
                    round={round}
                    scores={scores}
                    isHost={isHost}
                    chatterVotes={chatterVotes}
                    playerAvatars={playerAvatars}
                    isMaximized={scoringMaximized}
                    onIsMaximizedChange={setScoringMaximized}
                    floatingGridPos={scoringFloatingPos}
                    onFloatingGridPosChange={setScoringFloatingPos}
                    floatingPanelW={scoringFloatingW}
                    onFloatingPanelWChange={setScoringFloatingW}
                    displayMode={displayMode}
                  />
                )
              )}
              {round.phase === ROUND_PHASE.REVEALING && (
                <RevealingPhase
                  game={game}
                  round={round}
                  scores={scores}
                  isHost={isHost}
                  playerAvatars={playerAvatars}
                  isChatter={isChatter}
                  displayMode={displayMode}
                />
              )}
              {round.phase === ROUND_PHASE.ROUND_RESULTS && (
                <RoundResults game={game} round={round} scores={scores} isHost={isHost} playerAvatars={playerAvatars} isChatter={isChatter} />
              )}
            </>
          )}
        </>
      )}

      {/* Game over */}
      {game.status === GAME_STATUS.COMPLETED && (
        <GameOver game={game} isChatter={isChatter} playerAvatars={playerAvatars} />
      )}


      {/* Floating game UI buttons — fixed bottom-left, z-40, floats above all game content */}
      {!scoringMaximized && isRegularPlayer && !isChatter && (
        <div className="fixed bottom-4 left-4 z-40 flex flex-col items-start gap-2 pointer-events-none">

          {/* Song queue section — button just above ADHD button; panel above button */}
          {!hideQueue && songQueueVisible && (
            <>
              {songQueueOpen && (
                <div className="pointer-events-auto w-72 max-h-[min(28rem,70dvh)] overflow-y-auto t-bg-surface t-border border rounded-xl shadow-xl">
                  <div className="px-4 pt-3 pb-4">
                    <SongQueuePanel
                      game={game}
                      userId={user.uid}
                      onQueueChange={setHasQueuedSong}
                    />
                  </div>
                </div>
              )}
              <button
                type="button"
                data-testid="song-queue-toggle"
                onClick={() => {
                  if (!songQueueOpen) setMiniGameOpen(false);
                  setSongQueueOpen((o) => !o);
                }}
                aria-expanded={songQueueOpen}
                aria-controls="song-queue-panel"
                className="pointer-events-auto text-sm t-text-muted hover:t-text transition-colors t-focus-ring rounded flex items-center gap-1.5 bg-transparent"
              >
                <span>🎵 {t('songQueue.panelLabel')}</span>
                {hasQueuedSong && (
                  <span className="t-bg-accent-muted t-text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                    {t('songQueue.queuedBadge')}
                  </span>
                )}
                <span aria-hidden="true" className="text-xs">{songQueueOpen ? '▲' : '▾'}</span>
              </button>
            </>
          )}

          {/* ADHD mode section — at the very bottom; panel above button */}
          {!hideMiniGame && miniGameShow && (
            <>
              {miniGameOpen && (
                <div id="adhd-minigame-panel" className="pointer-events-auto t-bg-surface t-border border rounded-xl shadow-xl p-3">
                  <MiniGame
                    userId={user?.uid}
                    initialHighScore={miniGameHighScoreOverride ?? profile?.miniGameHighScore ?? 0}
                    onHighScoreUpdate={setMiniGameHighScoreOverride}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  const next = !miniGameOpen;
                  if (next) setSongQueueOpen(false);
                  setMiniGameOpen(next);
                }}
                aria-expanded={miniGameOpen}
                aria-controls="adhd-minigame-panel"
                className="pointer-events-auto text-sm t-text-muted hover:t-text transition-colors t-focus-ring rounded flex items-center gap-1.5 bg-transparent"
              >
                <span>🎮 {t('miniGame.toggleButton')}</span>
                <span aria-hidden="true" className="text-xs">{miniGameOpen ? '▲' : '▾'}</span>
              </button>
            </>
          )}

        </div>
      )}

      <PermissionWarningWrapper />
      </div>
      </WebRTCProvider>
    </Layout>
  );
}

function PermissionWarningWrapper() {
  const webrtc = useWebRTC();
  if (!webrtc || !webrtc.pendingPermission) return null;
  return (
    <PermissionWarningModal
      type={webrtc.pendingPermission}
      onConfirm={webrtc.confirmPermission}
      onCancel={webrtc.cancelPermission}
    />
  );
}


function CopyRoomCode({ code }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('game.copyCode')}
      title={t('game.copyCode')}
      className="flex items-center gap-1 t-focus-ring rounded hover:t-text-accent transition-colors"
    >
      <span data-testid="room-code" className="font-mono font-bold t-text-secondary text-xs">{code}</span>
      <span className="text-xs t-text-muted">{copied ? '✓' : '📋'}</span>
    </button>
  );
}
